import type { OnboardingGoal, OnboardingPace } from "./plan";

export type UnitWeight = "kg" | "lbs";
export type UnitHeight = "cm" | "ftin";

/** Mutable bag of everything the flow collects; one instance lives in OnboardingFlow. */
export interface FlowAnswers {
  goalType?: OnboardingGoal;
  motivation?: string;
  impact?: string;
  weightKg?: number;
  targetWeightKg?: number;
  unitWeight: UnitWeight;
  unitHeight?: UnitHeight;
  heightCm?: number;
  age?: number;
  gender?: string;
  activityLevel?: string;
  exerciseFrequency?: string;
  exerciseType?: string;
  avgDailySteps?: number;
  bodyFatPercent?: number;
  waistCm?: number;
  safetyFlags?: string[];
  weightLossPace?: OnboardingPace;
  loggingStyle?: string;
  eatingStyle?: string;
}

export interface OptionItem {
  readonly value: string;
  readonly title: string;
  readonly sublabel?: string;
  readonly badge?: string;
}

export const KG_TO_LBS = 2.20462;
export const LBS_TO_KG = 0.453592;

export const GOAL_OPTIONS: readonly OptionItem[] = [
  { value: "lose", title: "Lose weight" },
  { value: "maintain", title: "Maintain weight" },
  { value: "gain", title: "Gain weight" },
  { value: "healthier", title: "Eat healthier" },
  { value: "muscle", title: "Build muscle" },
];

export const MOTIVATION_OPTIONS: readonly OptionItem[] = [
  { value: "confidence", title: "Feel more confident" },
  { value: "health", title: "Improve health" },
  { value: "appearance", title: "Look better" },
  { value: "energy", title: "Have more energy" },
  { value: "clothes", title: "Fit into my clothes again" },
  { value: "consistency", title: "Build consistency" },
  { value: "other", title: "Other" },
];

export const IMPACT_OPTIONS: readonly OptionItem[] = [
  { value: "confidence", title: "My confidence" },
  { value: "health", title: "My health" },
  { value: "energy", title: "My daily energy" },
  { value: "routine", title: "My routine" },
  { value: "food_relationship", title: "My relationship with food" },
];

export const GENDER_OPTIONS: readonly OptionItem[] = [
  { value: "male", title: "Male" },
  { value: "female", title: "Female" },
  { value: "prefer_not_to_say", title: "Prefer not to say" },
];

export const ACTIVITY_OPTIONS: readonly OptionItem[] = [
  { value: "sedentary", title: "Sedentary", sublabel: "Mostly seated, little structured exercise" },
  { value: "light", title: "Lightly active", sublabel: "Mostly seated, exercise a few times a week" },
  { value: "moderate", title: "Moderately active", sublabel: "Frequently walking or active during the day" },
  { value: "active", title: "Very active", sublabel: "Physically active job or regular intense exercise" },
  { value: "very_active", title: "Athlete", sublabel: "Intense training most days" },
];

export const EXERCISE_OPTIONS: readonly OptionItem[] = [
  { value: "rarely", title: "Rarely" },
  { value: "1-2", title: "1–2 times/week" },
  { value: "3-4", title: "3–4 times/week" },
  { value: "5+", title: "5+ times/week" },
];

export const EXERCISE_TYPE_OPTIONS: readonly OptionItem[] = [
  { value: "none", title: "Little or no structured exercise" },
  { value: "walking", title: "Mostly walking" },
  { value: "cardio", title: "Cardio — running, cycling, swimming" },
  { value: "resistance", title: "Strength / resistance training" },
  { value: "mixed", title: "A mix of cardio and strength" },
  { value: "sports", title: "Sports / athletic training" },
];

export const STEPS_OPTIONS: readonly OptionItem[] = [
  { value: "under_5000", title: "Under 5,000" },
  { value: "5000_7500", title: "5,000–7,500" },
  { value: "7500_10000", title: "7,500–10,000" },
  { value: "10000_plus", title: "10,000+" },
  { value: "unknown", title: "I don't know" },
];

/** Numeric midpoint persisted in avgDailySteps for each steps bucket ("unknown" → undefined). */
export const STEPS_BUCKET_MIDPOINTS: Record<string, number | undefined> = {
  under_5000: 3500,
  "5000_7500": 6250,
  "7500_10000": 8750,
  "10000_plus": 12000,
  unknown: undefined,
};

export function stepsBucketForValue(avgDailySteps: number): string {
  if (avgDailySteps < 5000) return "under_5000";
  if (avgDailySteps < 7500) return "5000_7500";
  if (avgDailySteps < 10000) return "7500_10000";
  return "10000_plus";
}

/** Exclusive "none of these" value for the safety multi-select. */
export const SAFETY_NONE_VALUE = "none";

export const SAFETY_OPTIONS: readonly OptionItem[] = [
  { value: "pregnancy", title: "I'm pregnant" },
  { value: "breastfeeding", title: "I'm breastfeeding" },
  { value: "under_18", title: "I'm under 18" },
  { value: "eating_disorder", title: "I have or am recovering from an eating disorder" },
  { value: "medical_restriction", title: "I follow a medically restricted diet" },
  { value: "medication_affecting_weight", title: "I take medication that affects my weight" },
];

export const PACE_OPTIONS: readonly OptionItem[] = [
  { value: "relaxed", title: "Comfortable", sublabel: "About 0.25% of body weight per week — easiest to sustain" },
  { value: "steady", title: "Moderate", sublabel: "About 0.5% per week — recommended", badge: "Recommended" },
  { value: "aggressive", title: "Aggressive", sublabel: "About 0.75% per week — demanding" },
];

export const LOGGING_OPTIONS: readonly OptionItem[] = [
  { value: "fastest", title: "Fastest possible", sublabel: "Photo-first, minimal typing" },
  { value: "balanced", title: "Balanced: fast + accurate", sublabel: "Photo plus quick confirmation", badge: "Recommended" },
  { value: "accurate", title: "Most accurate", sublabel: "Refine portions, ingredients, and cooking oil" },
];

export const EATING_OPTIONS: readonly OptionItem[] = [
  { value: "none", title: "No preference" },
  { value: "high_protein", title: "High protein" },
  { value: "low_carb", title: "Low carb" },
  { value: "vegetarian", title: "Vegetarian" },
  { value: "vegan", title: "Vegan" },
  { value: "keto", title: "Keto" },
  { value: "other", title: "Other" },
];

export function kgToDisplay(weightKg: number, unit: UnitWeight): number {
  return unit === "lbs" ? Math.round(weightKg * KG_TO_LBS * 10) / 10 : weightKg;
}

export function displayToKg(value: number, unit: UnitWeight): number {
  return unit === "lbs" ? Math.round(value * LBS_TO_KG * 10) / 10 : value;
}

const CM_PER_INCH = 2.54;

export function cmToFtIn(cm: number): { ft: number; inch: number } {
  const totalInches = Math.round(cm / CM_PER_INCH);
  return { ft: Math.floor(totalInches / 12), inch: totalInches % 12 };
}

export function ftInToCm(ft: number, inch: number): number {
  return Math.round((ft * 12 + inch) * CM_PER_INCH * 10) / 10;
}
